import React, { useState, useEffect, useRef } from 'react';
import "../../styles/components/auto-complete.css";

const Autocomplete = ({ suggestions, label, value, onChange }) => {
    const [filteredSuggestions, setFilteredSuggestions] = useState([]);
    const [inputValue, setInputValue] = useState(value || '');
    const autocompleteRef = useRef(null);

    // a little ref to know “we just clicked a suggestion”
    const justSelected = useRef(false);

    // Sync from parent
    useEffect(() => {
        setInputValue(value || '');
        // if parent pushes in a new value, treat it as if it wasn’t “just selected” here
        justSelected.current = false;
    }, [value]);

    // Helper: get the text after the last comma
    const lastSegment = (text) => {
        const parts = text.split(',');
        return parts[parts.length - 1].trimStart();
    };

    useEffect(() => {
      const handleClickOutside = (event) => {
        if (autocompleteRef.current && !autocompleteRef.current.contains(event.target)) {
          setFilteredSuggestions([]);
        }
      };
  
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside); // For mobile devices
  
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('touchstart', handleClickOutside);
      };
    }, [autocompleteRef]);

    const handleChange = (e) => {
        const full = e.target.value;
        setInputValue(full);
        onChange?.(full);

        // 1) If we just selected and the user hasn’t typed a comma yet, bail out
        if (justSelected.current && full.slice(-1) !== ',') {
            // drop the suggestions but don’t re‑filter
            justSelected.current = false;
            setFilteredSuggestions([]);
            return;
        }

        // 2) If they literally just typed a comma, we’re now ready to open a fresh segment
        if (full.slice(-1) === ',') {
            justSelected.current = false;
        }

        // 3) Now filter the *new* segment (if they’ve typed any letters)
        const segment = lastSegment(full).toLowerCase();
        if (!segment) {
            setFilteredSuggestions([]);
            return;
        }

        setFilteredSuggestions(
            suggestions.filter(s =>
                s.toLowerCase().includes(segment)
            )
        );
    };

    const handleSelect = (suggestion) => {
        // find the split point
        const idx = inputValue.lastIndexOf(',');
        // if there’s a comma, keep everything through it (incl. the comma and any spaces),
        // otherwise prefix is empty
        const prefix = idx >= 0
            ? inputValue.slice(0, idx + 1).trimEnd() + ' '
            : '';
        const newValue = prefix + suggestion;

        setInputValue(newValue);
        onChange?.(newValue);
        setFilteredSuggestions([]);
        justSelected.current = true;
    };

    return (
        <div className="autocomplete-form-group">
            <label htmlFor="autocomplete-input">{label}</label>
            <input
                id="autocomplete-input"
                type="text"
                value={inputValue}
                onChange={handleChange}
                placeholder="Enter Committee Head(s) Separated by Comma"
                className="autocomplete-input"
            />
            {filteredSuggestions.length > 0 && (
                <ul className="autocomplete-suggestions">
                    {filteredSuggestions.map((s, i) => (
                        <li
                            key={i}
                            className="autocomplete-suggestion"
                            onClick={() => handleSelect(s)}
                        >
                            {s}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

export default Autocomplete;
